/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client'

import type { ReactNode } from 'react'
import ConsoleDockSlot from '../../components/console-dock-slot.component'
import AuthenticatedLayout from '../../components/layouts/authenticated.layout'
import MainLayout from '../../components/layouts/main.layout'
import LegalReacceptanceBanner from '../../components/legal-reacceptance-banner.component'
import OrgInvitesBanner from '../../components/org-invites-banner.component'
import PendingInvitesProvider from '../../components/pending-invites-provider.component'
import PlatformLockdownGate from '../../components/platform-lockdown-gate.component'
import SecondaryNavBarComponent from '../../components/secondary-nav-bar.component'

/**
 * The console's authenticated shell (App Router route group, AGL-401). Every
 * signed-in page lived behind `[AuthenticatedLayout, MainLayout]` in the
 * Pages Router `.layouts` array; this folder layout provides that shell once.
 * This layout is a client component and so cannot carry `metadata`; document
 * titles come from the title-only server layout beside each route (AGL-1059).
 * Pages wrap their body in `DashboardLayout` (per-page header/breadcrumbs)
 * inside their page.
 *
 * The secondary app bar is deliberately NOT in there with them (AGL-755):
 * this is the only position above every route boundary in the group, so it is
 * also the only one where the site switcher survives a navigation.
 */
export default function AppLayout({ children }: { children: ReactNode }) {
  return (
    // Email/password accounts must verify before any console access
    // (AGL-479); OAuth accounts arrive verified, so this only gates them.
    <AuthenticatedLayout requireEmailVerification>
      {/* Platform lockdown notice (AGL-1501): server-side enforcement makes
          the app unusable for locked users; this swaps the resulting sea of
          failed requests for the notice. Staff pass through untouched. */}
      <PlatformLockdownGate>
        {/* One pending-invites list and one accept/decline dialog for the
            banner below and the notifications menu inside MainLayout. */}
        <PendingInvitesProvider>
          <MainLayout>
            <SecondaryNavBarComponent />
            {/* Re-acceptance when the published Terms move past what this
                account agreed to (AGL-2316). Mounted here for the same reason
                the secondary app bar is: it is the one position above every
                route boundary, so the ask survives a navigation instead of
                appearing on whichever page happened to render it. */}
            <LegalReacceptanceBanner />
            {/* Pending workspace invitations (AGL-3402), here for the same
                reason: an invitation to another workspace has to be answerable
                from inside the one the person is working in. */}
            <OrgInvitesBanner placement="shell" />
            {children}
            {/* The console dock (AGL-1860, AGL-2940): a floating panel on
                every console page — this is the one slot above every route
                boundary, same reason the secondary app bar lives here. A
                plugin owns what it draws there (the AI plugin's assistant);
                the shell resolves the scope and a verdict for any release
                flag the widget asks about, and hands them down. */}
            <ConsoleDockSlot />
          </MainLayout>
        </PendingInvitesProvider>
      </PlatformLockdownGate>
    </AuthenticatedLayout>
  )
}
